(() => {
  if (window.VianelliCardCount) return;

  const cache = new Map();
  const CACHE_LIMIT = 6;
  const REQUEST_TIMEOUT = 20000;
  const GRID_SELECTOR = '#product-grid[data-vianelli-card-count-enabled]';
  let current = null;

  const gridFor = (container) => container?.querySelector(GRID_SELECTOR);
  const paint = (total = null) => {
    document.querySelectorAll('[data-vianelli-card-count]').forEach((target) => {
      const template = total === 1 ? target.dataset.countTemplateOne : target.dataset.countTemplateOther;
      target.textContent = total === null
        ? target.dataset.countPending || ''
        : (template || 'COUNT_VALUE').replaceAll('COUNT_VALUE', String(total));
    });
  };
  const remember = (key, value) => {
    cache.delete(key);
    cache.set(key, value);
    if (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
  };
  const isCurrent = (state) => current === state && !state.aborter.signal.aborted &&
    state.container.isConnected && gridFor(state.container) === state.grid;
  const ensureCurrent = (state) => {
    if (!isCurrent(state)) throw new DOMException('Collection navigation changed.', 'AbortError');
  };
  const cancel = () => {
    current?.aborter.abort();
    current = null;
  };
  const countKey = (params) => {
    const relevant = new URLSearchParams(params);
    ['page', 'view', 'section_id', 'sections', 'sort_by', 'per_page', 'page_size'].forEach((name) => relevant.delete(name));
    relevant.sort();
    return `${location.pathname}?${relevant.toString()}`;
  };

  async function requestPage(state, page) {
    ensureCurrent(state);
    const url = new URL(location.pathname, location.origin);
    url.search = state.params.toString();
    ['page', 'view', 'section_id', 'sections'].forEach((name) => url.searchParams.delete(name));
    url.searchParams.set('view', state.grid.dataset.vianelliIndexView || 'vianelli-price-index');
    url.searchParams.set('page', String(page));
    const aborter = new AbortController();
    const abort = () => aborter.abort();
    state.aborter.signal.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, REQUEST_TIMEOUT);
    try {
      const response = await fetch(url, { credentials: 'same-origin', signal: aborter.signal });
      if (!response.ok) throw new Error(`Collection card count: HTTP ${response.status}`);
      const result = await response.json();
      ensureCurrent(state);
      if (aborter.signal.aborted) throw new DOMException('Collection count timed out.', 'AbortError');
      return result;
    } finally {
      clearTimeout(timer);
      state.aborter.signal.removeEventListener('abort', abort);
    }
  }

  async function readIndex(state) {
    const ids = new Set();
    let page = 1;
    while (page !== null) {
      const result = await requestPage(state, page);
      if (!Array.isArray(result.variants)) throw new Error('Invalid collection card index.');
      result.variants.forEach((entry) => {
        if (!entry || !['string', 'number'].includes(typeof entry.id) || String(entry.id).trim() === '') {
          throw new Error('Invalid card in collection index.');
        }
        ids.add(String(entry.id));
      });
      const nextPage = result.nextPage === null ? null : Number(result.nextPage);
      if (nextPage !== null && (!Number.isInteger(nextPage) || nextPage <= page || nextPage > 100)) {
        throw new Error('Invalid collection card index pagination.');
      }
      page = nextPage;
    }
    return ids.size;
  }

  function refresh(container = document.getElementById('ProductGridContainer'), options = {}) {
    if (!document.body.classList.contains('vianelli-collections')) return Promise.resolve(null);
    const grid = gridFor(container);
    if (!grid || !container.isConnected) return Promise.resolve(null);
    const params = new URLSearchParams(options.searchParams ?? location.search);
    const key = countKey(params);
    if (current?.grid === grid && current.key === key) {
      if (current.total !== null) {
        paint(current.total);
        return Promise.resolve(current.total);
      }
      if (current.promise) return current.promise;
      if (current.failed) { paint(); return Promise.resolve(null); }
    } else {
      cancel();
      current = { container, grid, params, key, aborter: new AbortController(), total: null, promise: null, failed: false };
    }
    const state = current;
    paint();
    const scalar = grid.getAttribute('data-vianelli-card-total');
    if (!grid.hasAttribute('data-vianelli-price-sort') && Number(grid.dataset.vianelliSourcePages) <= 1 &&
        scalar !== null && /^\d+$/.test(scalar) && Number.isSafeInteger(Number(scalar))) {
      state.total = Number(scalar);
      remember(key, { total: state.total });
      paint(state.total);
      return Promise.resolve(state.total);
    }
    state.promise = Promise.resolve().then(async () => {
      try {
        let total;
        if (grid.hasAttribute('data-vianelli-price-sort')) {
          const controller = window.VianelliVariantPriceSort?.controller(container);
          if (!controller) return null;
          await controller.ready;
          ensureCurrent(state);
          if (!Array.isArray(controller.entries)) throw new Error('Collection price count is unavailable.');
          total = controller.entries.length;
        } else if (cache.has(key)) {
          const cached = cache.get(key);
          if (cached.failed) { state.failed = true; return null; }
          total = cached.total;
        } else {
          total = await readIndex(state);
        }
        ensureCurrent(state);
        state.total = total;
        remember(key, { total });
        paint(total);
        return total;
      } catch (error) {
        if (isCurrent(state)) {
          state.failed = true;
          remember(key, { failed: true });
          paint();
        }
        return null;
      }
    }).finally(() => { state.promise = null; });
    return state.promise;
  }

  window.VianelliCardCount = { refresh, cancel };
  const boot = () => refresh();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('productsLoaded', boot);
})();
